import * as THREE from '@phdsoft/three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Injectable } from '@angular/core';
import { ToonShaderHatching } from 'three/examples/jsm/shaders/ToonShader.js';
import { Region } from 'src/app/platform/busines-data/Framework/Region';
import { SurfaceElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/SurfaceElement';
import { LinearElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/LinearElement';
import { MaterialType } from 'src/app/platform/busines-data/ResourceElements/MaterialType';
import { ThicknessColorTable } from 'src/app/platform/busines-data/ResourceElements/thickness-color-table';
import { RegionItem } from '@phdsoft/common';
import { WorkableElement } from 'src/app/platform/busines-data/SectionElements/WorkableElement';
import { GaugingElement } from 'src/app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { getDataTexCriticalArea, getSquareTex } from '../MeshUtils/DataTextureUtils';
import { GridShader } from '../Shader/gridShader';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { ICriticalAreaDTO, RegionRisk, SectionRisk } from 'src/app/template/critical-area-window/critical-area.service';
import { CriticalAreaElement } from 'src/app/platform/busines-data/CriticalAreaElement/CriticalAreaElement';
import { RiskLevelItem } from 'src/app/admin/pages/risk-setup/types/risk-config.model';
import { ThreeJsCleanup } from 'src/app/shared/utils/threejs-cleanup.util';
import { GraphicsMaterialsLegends } from './graphics-materials-legends';
import { CullingService } from './culling/culling.service';
import { GraphicsDebug } from './graphics-debug';
import { environment } from '../../../environments/environment';
import { MaterialName, RiskMaterialNamePattern, SUPPORTED_BRING_TO_FRONT_MATERIALS, SupportedBringToFrontMaterialName } from './graphics-materials.types';
import { FeaColorConfiguration } from 'src/app/fea/rendering/fea-color-configuration';
import { getLightSectionMeshBaseMaterial, setLightSectionMeshMaterial, syncMaskedMaterialsFromBase } from './lightsection-mask';
import { regionColorHex } from './region-color';

/**
 * Paint FEA colors onto ONE merged mesh from its baked FEA attributes.
 * Deliberately ignores mesh/group visibility: the occlusion culler toggles
 * mesh.visible per frame and MODEL mode keeps tube type-meshes hidden, so
 * filtering here starves meshes that become visible later. Painting a hidden
 * mesh is harmless — it just isn't rendered until shown.
 * No-ops when the geometry lacks baked FEA attributes (old-format GLB).
 */
export function paintFeaColorsOnMesh(
  mesh: THREE.Mesh,
  feaVector: Record<number, number>,
  colorConfig: { getColor(value: number): string },
  byNode: boolean,
  vcMaterial: THREE.Material,
  fallbackColor: THREE.Color,
  colorCache: Map<number, THREE.Color> = new Map()
): void {
  const geometry = mesh.geometry as THREE.BufferGeometry;
  // _EL_IDX-style names arrive lowercased + double-underscore from the loader.
  const nodeIdAttr = geometry.getAttribute('__node_id');
  const elemIdAttr = geometry.getAttribute('__elem_id');
  const nodeAAttr = geometry.getAttribute('__node_a_id');
  const nodeBAttr = geometry.getAttribute('__node_b_id');
  const elemTAttr = geometry.getAttribute('__elem_t');
  if (!nodeIdAttr && !elemIdAttr && !nodeAAttr && !nodeBAttr && !elemTAttr) return;

  // Memoize value -> color: chroma scale lookups are expensive and surfaces
  // repeat node values heavily across a plate's faces.
  const colorFor = (value: number): THREE.Color => {
    if (Number.isNaN(value)) return fallbackColor;
    let c = colorCache.get(value);
    if (!c) { c = new THREE.Color(colorConfig.getColor(value)); colorCache.set(value, c); }
    return c;
  };
  // id 0 is the conversor's zero-fill sentinel (element lacked FEA arrays);
  // a missing/undefined lookup maps to NaN -> fallback grey.
  const valueForId = (id: number): number => {
    if (!id) return NaN;
    const v = feaVector[id];
    return (v === undefined || v === null) ? NaN : v;
  };

  const vertexCount = geometry.getAttribute('position').count;
  const colors = new Float32Array(vertexCount * 3);
  for (let v = 0; v < vertexCount; v++) {
    let value: number;
    // A vertex is a tube-ring vertex when it carries a non-zero node_a/node_b
    // pair; surface vertices zero-fill those (harmonized meshes may hold both kinds).
    const isTubeVertex = !!(nodeAAttr && nodeBAttr) && (nodeAAttr.getX(v) !== 0 || nodeBAttr.getX(v) !== 0);
    if (byNode && isTubeVertex) {
      const va = valueForId(nodeAAttr.getX(v));
      const vb = valueForId(nodeBAttr.getX(v));
      const fa = !Number.isNaN(va), fb = !Number.isNaN(vb);
      value = fa && fb ? va + (vb - va) * (elemTAttr ? elemTAttr.getX(v) : 0) : fa ? va : fb ? vb : NaN;
    } else if (byNode) {
      value = valueForId(nodeIdAttr ? nodeIdAttr.getX(v) : 0);
    } else {
      value = valueForId(elemIdAttr ? elemIdAttr.getX(v) : 0);
    }
    const color = colorFor(value);
    colors[v * 3] = color.r; colors[v * 3 + 1] = color.g; colors[v * 3 + 2] = color.b;
  }

  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  const colorAttr = geometry.getAttribute('color');
  if (colorAttr instanceof THREE.BufferAttribute) colorAttr.needsUpdate = true;
  if (!mesh.userData._originalMaterial) mesh.userData._originalMaterial = getLightSectionMeshBaseMaterial(mesh);
  setLightSectionMeshMaterial(mesh, vcMaterial); // keeps the repair/custom-material mask on masked meshes
}

/**
 * NDC depth bias that pulls the light-model edge lines (GraphicsMaterials.lightModelEdgeMaterial)
 * toward the camera. The lines lie exactly on mesh triangle edges; polygonOffset only applies to
 * triangles in WebGL, and the mesh materials are shared app-wide, so the bias lives in the line
 * shader. 5e-6 ≈ 40 steps of the 24-bit depth buffer: covers the interpolation noise between a
 * line and a coincident face that has no polygonOffset of its own (structure view-mode materials
 * already push faces back by 3/40, slope-scaled). At a whole-ship view (near ≈ 124 m, light
 * model at 300-600 m) it is ≈ 1-4 cm in depth; with near = 0.5 m it is 0.05 m at 100 m.
 */
export const LIGHT_MODEL_EDGE_DEPTH_BIAS = 5e-6;

const MATERIAL_Z_OFFSETS: Record<MaterialName, { factor: number, units: number }> = {
  // Point materials (highest priority - closest to camera)
  'PointMaterial': { factor: -20, units: -20 },
  'PointMaterialRed': { factor: -20, units: -20 },
  'PointMaterialGreen': { factor: -20, units: -20 },
  'PointMaterialYellow': { factor: -20, units: -20 },
  'PointMaterialBlack': { factor: -20, units: -20 },
  'PointMaterialBlue': { factor: -20, units: -20 },
  'PointMaterialMagenta': { factor: -20, units: -20 },

  // Text and UI elements
  'TextMaterial': { factor: -15, units: 1 },
  'RulerLineMaterial': { factor: -50, units: 1 },

  // Inspection materials
  'BlueInspectionMaterial': { factor: -3.1, units: -3.2 },
  'GreyInspectionMaterial': { factor: -3, units: -3 },
  'RedInspectionMaterial': { factor: -4, units: -3 },
  'YellowInspectionMaterial': { factor: 0, units: -2 },
  'GreenInspectionMaterial': { factor: 0.5, units: 1 },
  'InspectionMaterial': { factor: 1, units: 2 },
  'OrphanInspectionMaterial': { factor: -3.5, units: -3.5 },

  // Critical areas
  'CriticalAreaMaterial': { factor: -5, units: -5 },
  'CriticalAreaBoxMaterial': { factor: -500, units: -500 },

  // Selection materials
  'SelectedBasicMaterial': { factor: -4, units: -4 },
  'SelectedBasicElementMaterial': { factor: -4, units: -4 },
  'SelectedAreaInspecMaterial': { factor: -4, units: -4 },
  'InspectionListElementMaterial': { factor: 1, units: 0.1 },

  // Protection elements
  'PaintMaterial': { factor: -2.75, units: -2.75 },
  'AnodeMaterial': { factor: -4, units: -4 },
  'ProtectionElementsMaterial': { factor: -4, units: -4 },
  'AnodeHighlightMaterial': { factor: 1, units: 0.1 },
  'AnodeLowlightMaterial': { factor: 1, units: 0.1 },
  'SensorHighlightMaterial': { factor: 1, units: 0.1 },

  // Anomaly materials
  'GreyAnomalyMaterial': { factor: -2, units: -2 },
  'RedAnomalyMaterial': { factor: -2, units: -2 },
  'OrangeAnomalyMaterial': { factor: -2, units: -2 },
  'YellowAnomalyMaterial': { factor: -2, units: -2 },
  'BlueAnomalyMaterial': { factor: -2, units: -2 },
  'GreenAnomalyMaterial': { factor: -2, units: -2 },

  // Repair materials
  'RepairBasicMaterial': { factor: -2, units: -2 },
  'HatchingMaterial': { factor: 2, units: 18 },
  'ExecutedRepairMaterial': { factor: 2.5, units: 40 },
  'CutPreviewMaterial': { factor: -4, units: -4 },
  'DoublerExecutedRepairMaterial': { factor: 2, units: 35 },

  // 4D structure materials (slightly higher than BasicMaterial)
  'Structure4DMaterial': { factor: 0.5, units: 28 },

  // Structure view-mode materials (behind inspections)
  'AcceptableCorrosionViewModeMaterial': { factor: 3, units: 40 },
  'SubstantialCorrosionViewModeMaterial': { factor: 3, units: 40 },
  'ExcessiveCorrosionViewModeMaterial': { factor: 3, units: 40 },

  // Basic structure materials (lowest  priority - furthest from camera)
  'BasicMaterial': { factor: 3, units: 40 },
  'BasicPhysicalMaterial': { factor: 1, units: 1 },
  'LightModelMaterial': { factor: 2, units: 1 },
  'StainlessSteelMaterial': { factor: 1, units: 1 },
  'StructureMaterial': { factor: 0, units: 0 }, // No offset for base structure
  'StiffenerMaterial3d': { factor: 3, units: 1 },
  'BracketMaterial3d': { factor: 2, units: 1 },
  'FlangeMaterial3d': { factor: 2, units: 1 },
  'SensorMaterial': { factor: 1, units: 1 },
  'SensorXrayMaterial': { factor: 1, units: 1 },
  'AnomalyGraphicsBasicMaterial': { factor: 1, units: 1 },

  // FEA mesh materials
  'FeaMeshMaterial': { factor: -10, units: -10 },
  'FemMaterial': { factor: 1, units: 0 },

  // Default for materials not specified
  'default': { factor: 0, units: 0 }
} as const;

const STRUCTURE_VIEW_MODE_Z_OFFSET = MATERIAL_Z_OFFSETS['BasicMaterial'];

@Injectable({ providedIn: 'root' })
export class GraphicsMaterials {
  public static opacityLevel: number = 1;
  private static doublerDarkenFactor: number = 0.85;

  public pdfSpriteMaterial: THREE.SpriteMaterial;
  public wrdSpriteMaterial: THREE.SpriteMaterial;
  public ytbSpriteMaterial: THREE.SpriteMaterial;
  public imgSpriteMaterial: THREE.SpriteMaterial;
  public exlSpriteMaterial: THREE.SpriteMaterial;
  public altSpriteMaterial: THREE.SpriteMaterial;

  private materialsRenderMaterial: Record<string, THREE.Material>;
  private thicknessRenderMaterial: Record<string, THREE.Material>;
  private regionsRenderMaterial: Record<string, THREE.Material>;
  private structure4DRenderMaterial: Record<string, THREE.Material>;

  /** Shared material for merged meshes using per-vertex colors */
  private _vertexColorMaterial: THREE.MeshStandardMaterial | null = null;

  public debugGui: GraphicsDebug | null = null;

  private static criticalAreasOffset: Map<string, { factor: number, units: number }> = new Map();

  private static readonly BRING_TO_FRONT_SUPPORTED_MATERIALS: ReadonlySet<SupportedBringToFrontMaterialName> =
    new Set(SUPPORTED_BRING_TO_FRONT_MATERIALS);

  constructor(private legendsService: GraphicsMaterialsLegends, private cullingService: CullingService) {
    this.initMaterials();
    this.materialsRenderMaterial = {};
    this.thicknessRenderMaterial = {};
    this.regionsRenderMaterial = {};
    this.structure4DRenderMaterial = {};
  }

  // While this service remains global, we cannot destroy the fields that are initialized at the constructor
  // without breaking places that use it. Keep method for reference and use after future refactoring!
  public destroy(): void {
    // Dispose debug GUI
    if (this.debugGui) {
      this.debugGui.destroy();
      this.debugGui = null;
    }

    // Dispose of all managed materials
    [
      this.pdfSpriteMaterial, this.wrdSpriteMaterial, this.ytbSpriteMaterial, this.imgSpriteMaterial,
      this.exlSpriteMaterial, this.altSpriteMaterial
    ].forEach((material) => {
        ThreeJsCleanup.disposeMaterial(material);
    });

    // Dispose and clear material containers
    [
      this.materialsRenderMaterial, this.thicknessRenderMaterial, this.regionsRenderMaterial, this.structure4DRenderMaterial
    ].forEach((container) => {
        Object.values(container).forEach((material) => ThreeJsCleanup.disposeMaterial(material));
        Object.keys(container).forEach((key) => delete container[key]);
    });
  }

  public static axisBoxMaterial = new THREE.MeshBasicMaterial({
    name: 'AxisBoxMaterial',
    color: 0xd4d4d4,
    side: THREE.DoubleSide,
    opacity: 0.3,
    transparent: true
  });

  public static axisBoxVectorMaterial = new THREE.LineBasicMaterial({
    name: 'AxisBoxVectorMaterial',
    color: 0xffffff,
    linewidth: 1,
    opacity: .5,
    transparent: true
  })

  public static axisBoxLabelMaterial = new THREE.MeshBasicMaterial({
    name: 'AxisBoxLabelMaterial',
    color: 0xffffff
  });

  public static greenDebugMaterial = new THREE.MeshBasicMaterial({
    name: 'GreenDebugMaterial',
    color: 0x00ff00
  });

  public static blueWireframeDebugMaterial = new THREE.MeshBasicMaterial({
    name: "BlueWireframeDebugMaterial",
    wireframe: true,
    color: 0x0000ff
  });


  private static commonPointMatProp = {
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -20,
    polygonOffsetUnits: -20
  }


  public static pointMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterial',
    color: 0xe5e5e5,
    ...GraphicsMaterials.commonPointMatProp
  })

  public static pointMaterialGreen = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterialGreen',
    color: 0x1c7024,
    ...GraphicsMaterials.commonPointMatProp
  })

  public static pointMaterialYellow = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterialYellow',
    color: 0xf2ff00,
    ...GraphicsMaterials.commonPointMatProp
  })

  public static pointMaterialRed = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterialRed',
    color: 0xff0000,
    ...GraphicsMaterials.commonPointMatProp
  })

  public static pointMaterialBlack = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterialBlack',
    color: 0x000000,
    ...GraphicsMaterials.commonPointMatProp
  })

  public static pointMaterialBlue = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterialBlue',
    color: 0x0066ff,
    ...GraphicsMaterials.commonPointMatProp
  })


  private static commonGridCreationMatProp = {
    opacity: 0.4,
    transparent: true
  }
  public static gridCreationMaterial = new THREE.MeshPhongMaterial({
    name: 'GridCreationMaterial', color: 0x00ff00, ...GraphicsMaterials.commonGridCreationMatProp
  })

  public static gridCreationCollisionMaterial = new THREE.MeshPhongMaterial({
    name: 'GridCreationCollisionMaterial', color: 0xff0000, ...GraphicsMaterials.commonGridCreationMatProp
  })

  public static gridCreationNeighnoorMaterial = new THREE.MeshPhongMaterial({
    name: 'GridCreationNeighnoorMaterial', color: 0x0000ff, ...GraphicsMaterials.commonGridCreationMatProp
  })

  public static gridCreationYellowMaterial = new THREE.MeshPhongMaterial({
    name: 'GridCreationYellowMaterial', color: 0xffff00, ...GraphicsMaterials.commonGridCreationMatProp
  })


  private static commonColorMatProp = {
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.4,
    side: THREE.DoubleSide
  }

  public static redMaterial = new THREE.MeshStandardMaterial({
    name: 'RedMaterial', color: 0xff0000, ...GraphicsMaterials.commonColorMatProp
  });

  public static orangeMaterial = new THREE.MeshStandardMaterial({
    name: 'OrangeMaterial', color: 0xe56b06, ...GraphicsMaterials.commonColorMatProp
  });

  public static yellowMaterial = new THREE.MeshStandardMaterial({
    name: 'YellowMaterial', color: 0xf2ff00, ...GraphicsMaterials.commonColorMatProp
  });

  public static greenMaterial = new THREE.MeshStandardMaterial({
    name: 'GreenMaterial', color: 0x1c7024, ...GraphicsMaterials.commonColorMatProp
  });

  public static blueMaterial = new THREE.MeshStandardMaterial({
    name: 'BlueMaterial', color: 0x1803ff, ...GraphicsMaterials.commonColorMatProp
  });

  public static blackMaterial = new THREE.MeshStandardMaterial({
    name: 'BlackMaterial', color: 0x000000, ...GraphicsMaterials.commonColorMatProp
  });

  public static acceptableCorrosionViewModeMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: 'AcceptableCorrosionViewModeMaterial', color: 0x1c7024, ...GraphicsMaterials.commonColorMatProp
  });

  public static substantialCorrosionViewModeMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: 'SubstantialCorrosionViewModeMaterial', color: 0xf2ff00, ...GraphicsMaterials.commonColorMatProp
  });

  public static excessiveCorrosionViewModeMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: 'ExcessiveCorrosionViewModeMaterial', color: 0xff0000, ...GraphicsMaterials.commonColorMatProp
  });

  // public static inspectionMaterial = new THREE.MeshPhongMaterial({
  //   specular: 0x444444,
  //   normalScale: new THREE.Vector2(1, 1),
  //   shininess: 30,
  //   transparent: true,
  //   opacity: 0.7,
  //   depthTest: true,
  //   depthWrite: false,
  //   polygonOffset: true,
  //   polygonOffsetFactor: -4,
  //   polygonOffsetUnits: -4,
  //   wireframe: false,
  //   side: THREE.DoubleSide,
  // });

  private static commonInspecMatProp = {
    polygonOffset: true,
    // polygonOffsetFactor: -3, polygonOffsetUnits: -3,
    transparent: false,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  }

  public static inspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, { // MeshStandardMaterial do not have roughness and metalness properties
    name: 'InspectionMaterial',
    color: 0xD2D2D2,
    transparent: false,
    opacity: GraphicsMaterials.opacityLevel,
    side: THREE.DoubleSide
  });

  // public static redInspectionMaterial = new THREE.MeshStandardMaterial(
  //   { name: 'RedInspectionMaterial', color: 0xf44236, polygonOffsetFactor: -4, polygonOffsetUnits: -3, ...GraphicsMaterials.commonInspecMatProp });

  // public static orangeInspectionMaterial = new THREE.MeshStandardMaterial(
  //   { name: 'OrangeInspectionMaterial', color: 0xFFA500, polygonOffsetFactor: -3.2, polygonOffsetUnits: -3.4, ...GraphicsMaterials.commonInspecMatProp });

  // public static yellowInspectionMaterial = new THREE.MeshStandardMaterial(
  //   { name: 'YellowInspectionMaterial', color: 0xf4c22b, polygonOffsetFactor: -3.15, polygonOffsetUnits: -3.3, ...GraphicsMaterials.commonInspecMatProp });

  // public static blueInspectionMaterial = new THREE.MeshStandardMaterial(
  //   { name: 'BlueInspectionMaterial', color: 0x04a9f5, polygonOffsetFactor: -3.1, polygonOffsetUnits: -3.2, ...GraphicsMaterials.commonInspecMatProp });

  // public static greenInspectionMaterial = new THREE.MeshStandardMaterial(
  //   { name: 'GreenInspectionMaterial', color: 0x22c650, polygonOffsetFactor: -3.05, polygonOffsetUnits: -3.1, ...GraphicsMaterials.commonInspecMatProp });

  // public static greyInspectionMaterial = new THREE.MeshStandardMaterial(
  //   { name: 'GreyInspectionMaterial', color: 0x8A8A8A, polygonOffsetFactor: -3, polygonOffsetUnits: -3, ...GraphicsMaterials.commonInspecMatProp });


  public static redInspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'RedInspectionMaterial',
    map: getSquareTex(new THREE.Color(0xf44236), new THREE.Color(0xD2D2D2)),
    ...GraphicsMaterials.commonInspecMatProp
  });


  public static yellowInspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'YellowInspectionMaterial',
    map: getSquareTex(new THREE.Color(0xffff00), new THREE.Color(0xD2D2D2)),
    ...GraphicsMaterials.commonInspecMatProp
  });

  public static blueInspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'BlueInspectionMaterial',
    map: getSquareTex(new THREE.Color(0x04a9f5), new THREE.Color(0xD2D2D2)),
    ...GraphicsMaterials.commonInspecMatProp
  });

  public static greenInspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'GreenInspectionMaterial',
    map: getSquareTex(new THREE.Color(0x22c650), new THREE.Color(0xD2D2D2)),
    ...GraphicsMaterials.commonInspecMatProp
  });

  public static greyInspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'GreyInspectionMaterial',
    map: getSquareTex(new THREE.Color(0x8A8A8A), new THREE.Color(0xD2D2D2)),
    ...GraphicsMaterials.commonInspecMatProp
  });

  public static orphanInspectionMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'OrphanInspectionMaterial',
    color: 0xFF00FF,
    // map: getSquareTex(new THREE.Color(0xFF00FF), new THREE.Color(0xD2D2D2)),
    ...GraphicsMaterials.commonInspecMatProp
  });

  public static pointMaterialMagenta = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: 'PointMaterialMagenta',
    color: 0xFF00FF,
    ...GraphicsMaterials.commonPointMatProp
  });


  private static commonAnomalyMatProp = {
    polygonOffset: true,
    polygonOffsetFactor: -2, // positive value pushes polygon further away
    polygonOffsetUnits: -2,
    side: THREE.DoubleSide
  }

  public static greyAnomalyMaterial = new THREE.MeshBasicMaterial({
    name: 'GreyAnomalyMaterial', color: 0x8A8A8A, ...GraphicsMaterials.commonAnomalyMatProp
  });

  public static redAnomalyMaterial = new THREE.MeshBasicMaterial({
    name: 'RedAnomalyMaterial', color: 0xf44236, ...GraphicsMaterials.commonAnomalyMatProp
  });

  public static orangeAnomalyMaterial = new THREE.MeshBasicMaterial({
    name: 'OrangeAnomalyMaterial', color: 0xFF8C00, ...GraphicsMaterials.commonAnomalyMatProp
  });

  public static yellowAnomalyMaterial = new THREE.MeshBasicMaterial({
    name: 'YellowAnomalyMaterial', color: 0xf4c22b, ...GraphicsMaterials.commonAnomalyMatProp
  });

  public static blueAnomalyMaterial = new THREE.MeshBasicMaterial({
    name: 'BlueAnomalyMaterial', color: 0x04a9f5, ...GraphicsMaterials.commonAnomalyMatProp
  });

  public static greenAnomalyMaterial = new THREE.MeshBasicMaterial({
    name: 'GreenAnomalyMaterial', color: 0x22c650, ...GraphicsMaterials.commonAnomalyMatProp
  });


  private static commonRiskMatProp = {
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    side: THREE.DoubleSide,
  }


  private static commonLinearElementMatProp = {
    dashed: false,
    dashScale: 2,
    dashSize: 1,
    gapSize: 1,
    linewidth: 2.9,
  }

  public static stiffenerMaterial = new LineMaterial({
    name: 'StiffenerMaterial', color: 0x006666, ...GraphicsMaterials.commonLinearElementMatProp
  });

  public static bracketMaterial = new LineMaterial({
    name: 'BracketMaterial', color: 0x008080, ...GraphicsMaterials.commonLinearElementMatProp
  });

  public static flangeMaterial = new LineMaterial({
    name: 'FlangeMaterial', color: 0x8b008b, ...GraphicsMaterials.commonLinearElementMatProp
  });

  public static get randomMaterial(): THREE.MeshBasicMaterial {
    var randomColor = Math.floor(Math.random() * 16777215).toString(16);
    return new THREE.MeshBasicMaterial({ name: 'RandomMaterial', color: Number('0x' + randomColor), side: THREE.DoubleSide });
  }

  public static planeMaterial = new THREE.MeshBasicMaterial({
    name: 'PlaneMaterial',
    color: 0x0000ff,
    side: THREE.DoubleSide,
    opacity: 0.4,
    transparent: true
  });

  public static paintMaterial = new THREE.MeshStandardMaterial({
    name: "PaintMaterial",
    color: 0xff0000,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.4,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2.75,
    polygonOffsetUnits: -2.75,
  });

  public static anodeMaterial = new THREE.MeshStandardMaterial({
    name: "AnodeMaterial",
    color: 0xB45F06,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.4,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });

  public static anodeHighlightMaterial = new THREE.MeshStandardMaterial({
    name: "AnodeHighlightMaterial",
    color: 0xe714e8,
    emissive: 0xe74ce0,
    emissiveIntensity: 0.5,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 0.1,
    transparent: false,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  })

  public static anodeLowlightMaterial = new THREE.MeshPhongMaterial({
    name: "AnodeLowlightMaterial",
    color: 0xe714e8,
    emissive: 0x000000,
    emissiveIntensity: 0.7,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 0.1,
    transparent: false,
    side: THREE.DoubleSide,
    // specular: 0xe74ce0,
    // color: 0xe74ce0,
    // normalScale: new THREE.Vector2(1, 1),
    // shininess: 30,
    // transparent: true,
    // opacity: 0.7,
    // depthTest: true,
    // depthWrite: false,
    // polygonOffset: true,
    // wireframe: false,
    // side: THREE.DoubleSide,
  })

  public static protectionElementsMaterial = new THREE.MeshStandardMaterial({
    name: "ProtectionElementsMaterial",
    color: 0xB45F06,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.4,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });

  public static inspectionListElementMaterial = new THREE.MeshStandardMaterial({
    name: "InspectionListElementMaterial",
    color: 0x0000ff,
    emissive: 0x992900,
    emissiveIntensity: 1,
    polygonOffset: true,
    polygonOffsetFactor: 1, // positive value pushes polygon further away
    polygonOffsetUnits: 0.1,
    transparent: false,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  })

  public static structureMaterial = new THREE.MeshStandardMaterial({
    name: "StructureMaterial",
    color: 0xe78c2d,
    transparent: true,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  })

  public static pipeMaterial = new THREE.MeshStandardMaterial({
    name: "PipeMaterial",
    color: 0x888888,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.4,
    side: THREE.DoubleSide,
  });

  public static basicMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "BasicMaterial",
    color: 0xB0E0E6,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static doublerBasicMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "DoublerBasicMaterial",
    color: new THREE.Color(0xB0E0E6).multiplyScalar(GraphicsMaterials.doublerDarkenFactor),
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static executedRepairMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "ExecutedRepairMaterial",
    color: 0xB0E0E6,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
    flatShading: true,
  });

  public static doublerExecutedRepairMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "DoublerExecutedRepairMaterial",
    color: new THREE.Color(0xB0E0E6).multiplyScalar(GraphicsMaterials.doublerDarkenFactor),
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
    flatShading: true,
  });

  public static rulerLineMaterial = new THREE.LineBasicMaterial({
    name: "RulerLineMaterial",
    color: 0xffffff,
    polygonOffset: true,
    polygonOffsetFactor: -50, // positive value pushes polygon further away
    polygonOffsetUnits: 1,
    linewidth: 3,
    side:THREE.DoubleSide,

  })

  public static lightModelMaterial = new THREE.MeshStandardMaterial({
    name: "LightModelMaterial",
    color: 0xB0E0E6,
    polygonOffset: true,
    polygonOffsetFactor: 2, // positive value pushes polygon further away
    polygonOffsetUnits: 1,
    transparent: true,
    opacity: 0.25,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static criticalAreaMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: "CriticalAreaMaterial",
    map: getDataTexCriticalArea(),
    transparent: true,
    side: THREE.DoubleSide,
  });

  public static criticalAreaBoxMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "CriticalAreaBoxMaterial",
    color: 0xf44236,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
    // depthTest: true,
  });

  public static repairBasicMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "RepairBasicMaterial",
    color: 0xB0E0E6,
    transparent: false,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static basicPhysicalMaterial = new THREE.MeshPhysicalMaterial({
    name: "BasicPhysicalMaterial",
    color: 0xB0E0E6,
    transparent: true,
    //depthTest: false,
    depthWrite: true,
    opacity: GraphicsMaterials.opacityLevel,
    clearcoatRoughness: 0,
    reflectivity: 0,
    roughness: 1.0,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static basicMaterialInternal = new THREE.MeshStandardMaterial({
    name: "BasicMaterialInternal",
    color: 0x5692a5,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static selectedBasicMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "SelectedBasicMaterial",
    color: 0xf3ad3f,
    emissive: 0xFF4500,
    emissiveIntensity: 0.5,
    transparent: false,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  });

  public static selectedBasicElementMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "SelectedBasicElementMaterial",
    color: 0x0000ff,
    emissive: 0x992900,
    emissiveIntensity: 0.5,
    transparent: false,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  });

  public static areaInspecMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "AreaInspecMaterial",
    color: 0xB0E0E6,
    transparent: false,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static selectedAreaInspecMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "SelectedAreaInspecMaterial",
    color: 0x0000ff,
    emissive: 0x992900,
    emissiveIntensity: 0.5,
    transparent: false,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  });


  public static selectionBoxMaterial = new THREE.MeshBasicMaterial({
    name: "SelectionBoxMaterial",
    color: 0x80b080,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.6,
  })

  // Projected repair-area preview: drawn directly on the element surface, so it relies on
  // polygon depth-bias (fill) and disabled depth-test (outline) instead of a geometric
  // offset, which would introduce view-angle parallax against the inserted area.
  // The fill is now a per-plate template: GraphicsRenderRepair clones it once per drag (one clone
  // per plate bias, kept across mouse moves; only the insertion-prism clipping planes are swapped
  // per update) with a bias relative to the plate material (see orthoPreviewFillClone); the -8/-8
  // below only applies to the template itself.
  public static orthoPreviewFillMaterial = new THREE.MeshBasicMaterial({
    name: "OrthoPreviewFillMaterial",
    color: 0x80b080,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -8,
    polygonOffsetUnits: -8,
  })

  // Depth-only proxies of the non-target members (girders, stiffeners) inside the projected
  // area: they write depth after the structure and before the fill, so the fill drawn on the
  // target plates does not show through the members standing in front of them. Must be
  // transparent: in three's opaque pass it would render before the (transparent) structure
  // and hide the members themselves.
  public static orthoPreviewDepthMaterial = new THREE.MeshBasicMaterial({
    name: "OrthoPreviewDepthMaterial",
    colorWrite: false,
    depthWrite: true,
    depthTest: true,
    transparent: true,
    polygonOffset: false,
    side: THREE.DoubleSide,
  })

  public static orthoPreviewLineMaterial = new THREE.LineBasicMaterial({
    name: "OrthoPreviewLineMaterial",
    color: 0xffffff,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false,
  })

  public static greenDocMaterial = new THREE.MeshBasicMaterial({
    name: 'GreenDocMaterial',
    color: 0x005000,
    side: THREE.DoubleSide
  });

  public static referenceLineMaterial = new LineMaterial({
    name: "ReferenceLineMaterial",
    color: 0x03cafc,
    dashed: true,
    dashScale: 1.4,
    dashSize: 2,
    gapSize: 1,
    linewidth: 3.9
  });

  public static stiffenerMaterial3d = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "StiffenerMaterial3d",
    color: 0x006666,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  })

  public static doublerStiffenerMaterial3d = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "DoublerStiffenerMaterial3d",
    color: new THREE.Color(0x006666).multiplyScalar(GraphicsMaterials.doublerDarkenFactor),
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static beamMaterial3d = new THREE.MeshStandardMaterial({
    name: "BeamMaterial3d",
    color: 0x004477,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static doublerBeamMaterial3d = new THREE.MeshStandardMaterial({
    name: "DoublerBeamMaterial3d",
    color: new THREE.Color(0x004477).multiplyScalar(GraphicsMaterials.doublerDarkenFactor),
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static bracketMaterial3d = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "BracketMaterial3d",
    color: 0x008080,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static doublerBracketMaterial3d = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "DoublerBracketMaterial3d",
    color: new THREE.Color(0x008080).multiplyScalar(GraphicsMaterials.doublerDarkenFactor),
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static flangeMaterial3d = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "FlangeMaterial3d",
    color: 0x8b008b,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static doublerFlangeMaterial3d = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "DoublerFlangeMaterial3d",
    color: new THREE.Color(0x8b008b).multiplyScalar(GraphicsMaterials.doublerDarkenFactor),
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static referenceLineMaterial3d = new THREE.MeshStandardMaterial({
    name: "ReferenceLineMaterial3d",
    color: 0x03cafc,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.9,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });

  public static borderMaterial = new LineMaterial({
    name: "BorderMaterial",
    color: 0x333333,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    linewidth: 1,
  });

  public static materialEdges = new THREE.LineBasicMaterial({
    name: "MaterialEdges",
    color: 0x000000,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    linewidth: 1.0
  });

  public static lineBasicMaterial = new THREE.LineBasicMaterial({
    name: "LineBasicMaterial",
    color: 0x0000ff,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    linewidth: 2,
  });

  public static borderMaterialLineBasic = new THREE.LineBasicMaterial({
    name: "BorderMaterialLineBasic",
    color: 0x333333,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    linewidth: 1,
  });

  /**
   * Light-model crease edges (AssetLoader → LightModelEdges), shared by every edge object. Same
   * look as the new-format section Borders, which the converter bakes as an opaque 0x333333
   * LineBasicMaterial (C4DWeb.GLTFConversor gltf/materials.js borderMaterialLineBasic; its
   * polygonOffset is not exported to the GLTF and would not apply to lines anyway). Default
   * depthFunc (LessEqual) plus LIGHT_MODEL_EDGE_DEPTH_BIAS in the vertex shader.
   */
  public static lightModelEdgeMaterial = GraphicsMaterials.createLightModelEdgeMaterial();

  public static textMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: "TextMaterial",
    color: 0xff3030,
    transparent: false,
    side: THREE.DoubleSide,
    clipIntersection: true,
    //depthTest: false
  });

  public static stainlessSteelMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "StainlessSteelMaterial",
    color: 0xB0E0E6,
    transparent: true,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static gridShaderMaterial = new THREE.ShaderMaterial({
    name: "GridShaderMaterial",
    uniforms: {
      thickness: {
        value: 1.5,
      },
    },
    vertexShader: GridShader.vertexShader,
    fragmentShader: GridShader.fragmentShader,
  });

  public static femMaterialwireFrame = new THREE.MeshBasicMaterial({
    name: "FemMaterialwireFrame",
    color: 0xabb4b8,
    wireframe: true,
    transparent: true,
    side: THREE.DoubleSide,
  });

  public static femMaterial = GraphicsMaterials.withOffset(THREE.MeshPhongMaterial, {
    name: "FemMaterial",
    color: 0xffffff,
    flatShading: true,
    vertexColors: true,
    shininess: 0,
    side: THREE.DoubleSide,
    reflectivity: 0.5,
  });


  public static feaMeshMaterial = GraphicsMaterials.withOffset(THREE.LineBasicMaterial, {
    name: 'FeaMeshMaterial',
    color: 0x333333,
    linewidth: 1,
    transparent: false,
    side: THREE.DoubleSide,
  });

  public static decalMaterial = new THREE.MeshPhongMaterial({
    name: "DecalMaterial",
    specular: 0x444444,
    normalScale: new THREE.Vector2(1, 1),
    shininess: 30,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: - 4,
    wireframe: false,
    side: THREE.DoubleSide
  });

  public static anomalyGraphicsBasicMaterial = GraphicsMaterials.withOffset(THREE.MeshBasicMaterial, {
    name: "AnomalyGraphicsBasicMaterial",
    color: 0xc2dcdf,
    side: THREE.DoubleSide
  });

  public static sensorHighlightMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "SensorHighlightMaterial",
    color: 0xffed00,
    emissive: 0x992900,
    emissiveIntensity: 0.5,
    transparent: false,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  })

  public static sensorLowlightMaterial = new THREE.MeshPhongMaterial({
    name: "SensorLowlightMaterial",
    specular: 0x444444,
    color: 0xffffff,
    normalScale: new THREE.Vector2(1, 1),
    shininess: 30,
    transparent: true,
    opacity: 0.7,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    wireframe: false,
    side: THREE.DoubleSide,
  })

  public static sensorEmptyBarMaterial = new THREE.MeshStandardMaterial({
    name: "SensorEmptyBarMaterial",
    color: 0xffffff,
    transparent: true,
    opacity: 0.0,
    roughness: 0.9,
    metalness: 0.4,
    side: THREE.DoubleSide,
  });

  public static sensorXrayMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "SensorXrayMaterial",
    color: 0xffffff,
    transparent: false,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static sensorMaterial = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
    name: "SensorMaterial",
    color: 0x006666,
    transparent: false,
    opacity: GraphicsMaterials.opacityLevel,
    roughness: 0.85,
    metalness: 0.5,
    side: THREE.DoubleSide,
  });

  public static selectedLineMaterial = new LineMaterial({
    name: "SelectedLineMaterial",
    color: 0xffffff,
    dashed: false,
    dashScale: 2,
    dashSize: 1,
    gapSize: 1,
    linewidth: 2.9,
  });

  public static selectedLineMaterialDashed = new LineMaterial({
    name: 'SelectedLineMaterialDashed',
    color: 0xffffff,
    dashed: true,
    dashScale: 10,
    dashSize: 1,
    gapSize: 1,
    linewidth: 2.9,
  });


  public static standardMaterialDashed = new LineMaterial({
    name: 'StandardMaterialDashed',
    dashed: true,
    dashScale: 10,
    dashSize: 1,
    gapSize: 1,
    linewidth: 2.9,
  });

  public static stiffenerMaterialDashed = new LineMaterial({
    name: 'StiffenerMaterialDashed',
    color: 0x006666,
    dashed: true,
    dashScale: 10,
    dashSize: 1,
    gapSize: 1,
    linewidth: 2.9,
  });

  public static bracketMaterialDashed = new LineMaterial({
    name: 'BracketMaterialDashed',
    color: 0x008080,
    dashed: true,
    dashScale: 10,
    dashSize: 1,
    gapSize: 1,
    linewidth: 2.9,
  });


  public static get hatchingMaterial(): THREE.ShaderMaterial {
    const _light = new THREE.DirectionalLight(0xffffff);
    const _ambientLight = new THREE.AmbientLight(0xed1111);
    // Convert ambient color to linear to match the new physically correct workflow
    _ambientLight.color.convertSRGBToLinear();

    // Convert hatching base color from sRGB (perceived) to Linear
    const baseColor = new THREE.Color(0.15, 0.48, 0.63);
    baseColor.convertSRGBToLinear();
    const hatchingcolor = [baseColor.r, baseColor.g, baseColor.b];

    const hatchingMaterial = GraphicsMaterials.createShaderMaterial(ToonShaderHatching, _light, _ambientLight, hatchingcolor);

    hatchingMaterial.name = 'HatchingMaterial';

    const offset = MATERIAL_Z_OFFSETS[hatchingMaterial.name] || MATERIAL_Z_OFFSETS['default'];
    hatchingMaterial.polygonOffset = true;
    hatchingMaterial.polygonOffsetFactor = offset.factor;
    hatchingMaterial.polygonOffsetUnits = offset.units;

    return hatchingMaterial;
  }

  public static get cutPreviewMaterial(): THREE.ShaderMaterial {
    const _light = new THREE.DirectionalLight(0xffffff);
    const _ambientLight = new THREE.AmbientLight(0xed1111);
    const previewColor = [0.9, 0.5, 0.1]; // Orange - distinct from repair teal

    const material = GraphicsMaterials.createShaderMaterial(ToonShaderHatching, _light, _ambientLight, previewColor);
    material.name = 'CutPreviewMaterial';

    const offset = MATERIAL_Z_OFFSETS[material.name] || MATERIAL_Z_OFFSETS['default'];
    material.polygonOffset = true;
    material.polygonOffsetFactor = offset.factor;
    material.polygonOffsetUnits = offset.units;

    return material;
  }


  /**
   * Load a colour (albedo/icon) texture. Image files are authored in sRGB; with
   * three's colour management + OutputPass they must be tagged as such or they
   * are treated as linear and rendered washed out.
   */
  public static loadColorTexture(loader: THREE.TextureLoader, url: string): THREE.Texture {
    const texture = loader.load(url);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  private initMaterials() {
    const textureLoader = new THREE.TextureLoader();
    const pdftexture = GraphicsMaterials.loadColorTexture(textureLoader, `//${document.location.host}/statics/img/pdf-ico.png`);
    const wrdtexture = GraphicsMaterials.loadColorTexture(textureLoader, `//${document.location.host}/statics/img/wrd-ico.png`);
    const ytbtexture = GraphicsMaterials.loadColorTexture(textureLoader, `//${document.location.host}/statics/img/ytb-ico.png`);
    const imgtexture = GraphicsMaterials.loadColorTexture(textureLoader, `//${document.location.host}/statics/img/img-ico.png`);
    const exltexture = GraphicsMaterials.loadColorTexture(textureLoader, `//${document.location.host}/statics/img/exl-ico.png`);
    const alttexture = GraphicsMaterials.loadColorTexture(textureLoader, `//${document.location.host}/statics/img/alt-ico.png`);


    this.pdfSpriteMaterial = new THREE.SpriteMaterial({ map: pdftexture });
    this.wrdSpriteMaterial = new THREE.SpriteMaterial({ map: wrdtexture });
    this.ytbSpriteMaterial = new THREE.SpriteMaterial({ map: ytbtexture });
    this.imgSpriteMaterial = new THREE.SpriteMaterial({ map: imgtexture });
    this.exlSpriteMaterial = new THREE.SpriteMaterial({ map: exltexture });
    this.altSpriteMaterial = new THREE.SpriteMaterial({ map: alttexture });
  }

  private static createShaderMaterial(shader, light, ambientLight, color) {
    const u = THREE.UniformsUtils.clone(shader.uniforms);
    const vs = shader.vertexShader;
    const fs = shader.fragmentShader;
    const material = new THREE.ShaderMaterial({ uniforms: u, vertexShader: vs, fragmentShader: fs });
    material.side = THREE.DoubleSide;
    material.uniforms["uDirLightPos"].value = light.position;
    material.uniforms["uDirLightColor"].value = light.color;
    material.uniforms["uAmbientLightColor"].value = ambientLight.color;
    // material.defaultAttributeValues['color'] = [1, 0, 0];

    material.uniforms["uBaseColor"].value = color
    return material;
  }

  public createMaterialMaterials(materials: Array<MaterialType>): void {
    for (const mat of materials) {
      this.materialsRenderMaterial[mat.Guid] = new THREE.MeshStandardMaterial({
        name: "RenderMaterial " + mat.materialSubType.Color,
        color: Number('0x' + mat.materialSubType.Color),
        polygonOffset: true,
        polygonOffsetFactor: STRUCTURE_VIEW_MODE_Z_OFFSET.factor,
        polygonOffsetUnits: STRUCTURE_VIEW_MODE_Z_OFFSET.units,
        transparent: true,
        opacity: GraphicsMaterials.opacityLevel,
        roughness: 0.85,
        metalness: 0.5,
        side: THREE.DoubleSide,
      });
    }
  }

  public createThicknessTableMaterials(thicknessColorTable: Array<ThicknessColorTable>): void {
    for (const tkC of thicknessColorTable) {
      this.thicknessRenderMaterial[tkC.thickness] = new THREE.MeshStandardMaterial({
        name: "ThicknessRenderMaterial " + tkC.Color,
        color: Number('0x' + tkC.Color),
        polygonOffset: true,
        polygonOffsetFactor: STRUCTURE_VIEW_MODE_Z_OFFSET.factor,
        polygonOffsetUnits: STRUCTURE_VIEW_MODE_Z_OFFSET.units,
        transparent: true,
        opacity: GraphicsMaterials.opacityLevel,
        roughness: 0.85,
        metalness: 0.5,
        side: THREE.DoubleSide,
      });
    }
  }

  public createRegionsMaterials(regions: Array<RegionItem>): void {
    for (const region of regions) {
      this.regionsRenderMaterial[region.id] = new THREE.MeshStandardMaterial({
        name: "RegionsRenderMaterial " + region.properties?.General?.Color,
        // UAT 2 N1: a region with General.Color null threw here and the digital-twin
        // catch-all logged the user out; regionColorHex falls back to a default grey.
        // color: Number('0x' + region.properties?.General?.Color.replace('#', '')),
        color: Number('0x' + regionColorHex(region.properties?.General?.Color)),
        polygonOffset: true,
        polygonOffsetFactor: STRUCTURE_VIEW_MODE_Z_OFFSET.factor,
        polygonOffsetUnits: STRUCTURE_VIEW_MODE_Z_OFFSET.units,
        transparent: true,
        opacity: GraphicsMaterials.opacityLevel,
        roughness: 0.85,
        metalness: 0.5,
        side: THREE.DoubleSide,
      });
    }
  }

  public defaultViewMode(regions: Array<Region>): void {
    GraphicsMaterials.basicMaterial.depthWrite = true;
    this.applyLightSectionTransparence(regions, false);
    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            obj.SurfaceDesign.material = obj.defaultMaterial;
            obj.currentMaterial = obj.defaultMaterial;
          }
        }
        for (const obj of section.linearElements) {
          if (obj instanceof LinearElement) {
            obj.SurfaceDesign.material = obj.defaultMaterial;
            obj.currentMaterial = obj.defaultMaterial;
          }
        }
      }
    }

    // Restore merged mesh materials in lightSectionGroup
    this.clearVertexColorsFromLightSection(regions);

    // Limpar legenda quando voltar ao modo padrão
    this.legendsService.clearLegend();
  }

  public resetStructureMaterials(regions: Array<Region>): void {
    GraphicsMaterials.basicMaterial.depthWrite = true;
    this.applyLightSectionTransparence(regions, false);
    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (obj.customMaterial) continue;  // preserve custom material slot [D-04]
            if (obj.isRepair) {
              obj.SurfaceDesign.material = obj.defaultMaterial;
              obj.currentMaterial = obj.defaultMaterial;
            } else {
              obj.SurfaceDesign.material = GraphicsMaterials.basicMaterial;
              obj.currentMaterial = GraphicsMaterials.basicMaterial;
              obj.defaultMaterial = GraphicsMaterials.basicMaterial;
            }
          }
        }
        for (const obj of section.linearElements) {
          if (obj instanceof LinearElement) {
            if (obj.customMaterial) continue;  // preserve custom material slot [D-04]
            if (obj.isRepair) {
              obj.SurfaceDesign.material = obj.defaultMaterial;
              obj.currentMaterial = obj.defaultMaterial;
            } else {
              if (obj.customMaterial) continue;  // preserve custom material slot [D-04]
              switch(obj.TypeId){
                case ELEMENTSTYPESIDS.STIFFENER:
                  obj.SurfaceDesign.material = GraphicsMaterials.stiffenerMaterial3d;
                  obj.currentMaterial = GraphicsMaterials.stiffenerMaterial3d;
                  obj.defaultMaterial = GraphicsMaterials.stiffenerMaterial3d;
                  break;
                case ELEMENTSTYPESIDS.BRACKET:
                  obj.SurfaceDesign.material = GraphicsMaterials.bracketMaterial3d;
                  obj.currentMaterial = GraphicsMaterials.bracketMaterial3d;
                  obj.defaultMaterial = GraphicsMaterials.bracketMaterial3d;
                  break;
                case ELEMENTSTYPESIDS.FACEPLATE:
                  obj.SurfaceDesign.material = GraphicsMaterials.flangeMaterial3d;
                  obj.currentMaterial = GraphicsMaterials.flangeMaterial3d;
                  obj.defaultMaterial = GraphicsMaterials.flangeMaterial3d;
                  break;
                case ELEMENTSTYPESIDS.BEAM:
                  obj.SurfaceDesign.material = GraphicsMaterials.beamMaterial3d;
                  obj.currentMaterial = GraphicsMaterials.beamMaterial3d;
                  obj.defaultMaterial = GraphicsMaterials.beamMaterial3d;
                  break;
                case ELEMENTSTYPESIDS.UNDEFINED:
                  obj.SurfaceDesign.material = GraphicsMaterials.redMaterial;
                  obj.currentMaterial = GraphicsMaterials.redMaterial;
                  obj.defaultMaterial = GraphicsMaterials.redMaterial;
                  break;
                default:
                  obj.SurfaceDesign.material = GraphicsMaterials.basicMaterial;
                  obj.currentMaterial = GraphicsMaterials.basicMaterial;
                  obj.defaultMaterial = GraphicsMaterials.basicMaterial;
              }
            }
          }
        }
      }
    }

    // Restore merged mesh materials in lightSectionGroup
    this.clearVertexColorsFromLightSection(regions);
  }

  public defaultViewModeLightModel(regions: Array<Region>): void {
    GraphicsMaterials.basicMaterial.depthWrite = true;
    this.applyLightSectionTransparence(regions, false);
    for (const region of regions) {
      for (const obj of region.surfaceElements) {
        if (obj instanceof SurfaceElement) {
          obj.SurfaceDesign.material = obj.defaultMaterial;
          obj.currentMaterial = obj.defaultMaterial;
        }
      }
    }

    // Restore merged mesh materials in lightSectionGroup
    this.clearVertexColorsFromLightSection(regions);
  }

  public regionsViewMode(regions: Array<Region>): void {

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            const material = this.regionsRenderMaterial[obj.regionId];
            obj.SurfaceDesign.material = material;
            obj.currentMaterial = material;
          }
        }
      }
    }

    // Color merged meshes in lightSectionGroup for MODEL/REGIONS modes
    const elementColorMap = this.buildElementColorMap(regions, (element) => {
      if (!(element instanceof SurfaceElement)) return null;
      const mat = this.regionsRenderMaterial[element.regionId] as THREE.MeshStandardMaterial;
      return mat?.color ?? null;
    });
    this.applyVertexColorsToLightSection(regions, elementColorMap);

    this.legendsService.generateRegionsLegend(regions, this.regionsRenderMaterial);
  }

  public regionsViewModeLightModel(regions: Array<Region>): void {
    for (const region of regions) {
      for (const obj of region.surfaceElements) {
        if (obj instanceof SurfaceElement) {
          obj.SurfaceDesign.material = this.regionsRenderMaterial[obj.regionId];
          obj.currentMaterial = this.regionsRenderMaterial[obj.regionId];
        }
      }
    }

    this.legendsService.generateRegionsLegendLightModel(regions, this.regionsRenderMaterial);
  }

  public materialsViewMode(regions: Array<Region>): void {

    for (const region of regions) {
      for (const section of region.sections) {

        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement && obj.MaterialType) {
            const material = this.materialsRenderMaterial[obj.MaterialType.Guid];
            obj.SurfaceDesign.material = material;
            obj.currentMaterial = material;
          }
        }

        for (const obj of section.linearElements) {
          if (obj instanceof LinearElement && obj.MaterialType) {
            obj.SurfaceDesign.material = this.materialsRenderMaterial[obj.MaterialType.Guid];
            obj.currentMaterial = this.materialsRenderMaterial[obj.MaterialType.Guid];
          }
        }
      }
    }

    // Color merged meshes in lightSectionGroup for MODEL/REGIONS modes
    const elementColorMap = this.buildElementColorMap(regions, (element) => {
      const mat = this.materialsRenderMaterial[element.MaterialType?.Guid] as THREE.MeshStandardMaterial;
      return mat?.color ?? null;
    });
    this.applyVertexColorsToLightSection(regions, elementColorMap);

    this.legendsService.generateMaterialsLegend(regions, this.materialsRenderMaterial);
  }

  public thicknessTableViewMode(regions: Array<Region>): void {

    for (const region of regions) {
      for (const section of region.sections) {

        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            const material = this.thicknessRenderMaterial[obj.thickness] ?? obj.defaultMaterial;
            obj.SurfaceDesign.material = material;
            obj.currentMaterial = material;
          }
        }

        for (const obj of section.linearElements) {
          if (obj instanceof LinearElement) {
            obj.SurfaceDesign.material = this.thicknessRenderMaterial[obj.thickness] ?? obj.defaultMaterial;
            obj.currentMaterial = obj.SurfaceDesign.material;
          }
        }
      }
    }

    // Color merged meshes in lightSectionGroup for MODEL/REGIONS modes
    const elementColorMap = this.buildElementColorMap(regions, (element) => {
      const mat = this.thicknessRenderMaterial[element.thickness] as THREE.MeshStandardMaterial;
      return mat ? mat.color : null;
    });
    this.applyVertexColorsToLightSection(regions, elementColorMap);

    this.legendsService.generateThicknessLegend(regions, this.thicknessRenderMaterial);
  }

  /**
   * Build a Map from element GUID (no dashes) to THREE.Color using a lookup function.
   * Reusable for thickness, material, region color modes, etc.
   */
  private buildElementColorMap(
    regions: Array<Region>,
    colorFn: (element: SurfaceElement | LinearElement) => THREE.Color | null
  ): Map<string, THREE.Color> {
    const colorMap = new Map<string, THREE.Color>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            const color = colorFn(obj);
            if (color) {
              colorMap.set(obj.Guid?.replace(/-/g, ''), color.clone());
            }
          }
        }
        for (const obj of section.linearElements) {
          if (obj instanceof LinearElement) {
            const color = colorFn(obj);
            if (color) {
              colorMap.set(obj.Guid?.replace(/-/g, ''), color.clone());
            }
          }
        }
      }
    }

    return colorMap;
  }

  /**
   * Apply per-vertex colors to all LightSection merged meshes using the _el_idx
   * attribute and elementMap userData to map vertices back to their original elements.
   */
  private applyVertexColorsToLightSection(
    regions: Array<Region>,
    elementColorMap: Map<string, THREE.Color>,
    fallbackColor: THREE.Color = new THREE.Color(0x888888)
  ): void {
    const vcMaterial = this.getVertexColorMaterial();

    for (const region of regions) {
      for (const section of region.sections) {
        if (!section.lightSectionGroup) continue;

        section.lightSectionGroup.traverse(child => {
          if (!(child instanceof THREE.Mesh)) return;

          const geometry = child.geometry as THREE.BufferGeometry;
          // _EL_IDX is lowercased to _el_idx by the GLTF loader
          const elIdxAttr = geometry.getAttribute('__el_idx');
          const elementMap = child.userData?.elementMap as Array<{ elementId: string }>;

          if (!elIdxAttr || !elementMap) return;

          const vertexCount = geometry.getAttribute('position').count;
          const colors = new Float32Array(vertexCount * 3);

          // Build index → color lookup from elementMap
          const indexColors: THREE.Color[] = new Array(elementMap.length);
          for (let i = 0; i < elementMap.length; i++) {
            const elementId = elementMap[i].elementId?.replace(/-/g, '');
            indexColors[i] = elementColorMap.get(elementId) ?? fallbackColor;
          }

          // Write per-vertex colors using _el_idx mapping
          for (let v = 0; v < vertexCount; v++) {
            const idx = elIdxAttr.getX(v);
            const color = idx < indexColors.length ? indexColors[idx] : fallbackColor;
            colors[v * 3] = color.r;
            colors[v * 3 + 1] = color.g;
            colors[v * 3 + 2] = color.b;
          }

          geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

          // Store the BASE material for restoration (masked meshes own a patched clone)
          if (!child.userData._originalMaterial) {
            child.userData._originalMaterial = getLightSectionMeshBaseMaterial(child);
          }

          setLightSectionMeshMaterial(child, vcMaterial);
        });
      }
    }

    this.paintLightSectionOverlays(regions, elementColorMap);
  }

  /**
   * Elements cut by a repair (or with a custom material) are masked out of the merged
   * lightsection meshes and drawn as overlays by LightSectionModRenderer, so the
   * vertex-colour repaint above never reaches them — in REGIONS they kept the default
   * structure colour while SECTIONS showed the view-mode colour. Give each overlay its
   * element's colour (a clone of its own material, so doublers keep their depth offset);
   * `null` restores the original materials.
   */
  private paintLightSectionOverlays(regions: Array<Region>, elementColorMap: Map<string, THREE.Color> | null): void {
    for (const region of regions) {
      for (const section of region.sections) {
        section.group?.traverse(child => {
          if (!(child instanceof THREE.Mesh) || !child.userData?.lightSectionOverlay) return;

          // drop the previous view-mode colour, back to the overlay's own material
          const previous = child.userData._viewModeMaterial as THREE.Material | undefined;
          if (previous) {
            child.material = child.userData._overlayBaseMaterial;
            previous.dispose();
            delete child.userData._viewModeMaterial;
            delete child.userData._overlayBaseMaterial;
          }

          const color = elementColorMap?.get(String(child.userData.elementId ?? '').replace(/-/g, ''));
          if (!color) return; // no view-mode colour for this element: keep its own material, as SECTIONS does

          const base = child.material as THREE.MeshStandardMaterial;
          const painted = (base.map ? GraphicsMaterials.basicMaterial : base).clone() as THREE.MeshStandardMaterial;
          painted.polygonOffset = base.polygonOffset;
          painted.polygonOffsetFactor = base.polygonOffsetFactor;
          painted.polygonOffsetUnits = base.polygonOffsetUnits;
          painted.color.copy(color);
          child.userData._overlayBaseMaterial = base;
          child.userData._viewModeMaterial = painted;
          child.material = painted;
        });
      }
    }
  }

  /**
   * Remove vertex colors from LightSection merged meshes and restore original materials.
   */
  private clearVertexColorsFromLightSection(regions: Array<Region>): void {
    this.paintLightSectionOverlays(regions, null);
    for (const region of regions) {
      for (const section of region.sections) {
        if (!section.lightSectionGroup) continue;

        section.lightSectionGroup.traverse(child => {
          if (!(child instanceof THREE.Mesh)) return;

          if (child.userData._originalMaterial) {
            setLightSectionMeshMaterial(child, child.userData._originalMaterial);
            delete child.userData._originalMaterial;
          }

          const geometry = child.geometry as THREE.BufferGeometry;
          if (geometry.getAttribute('color')) {
            geometry.deleteAttribute('color');
          }
        });
      }
    }
  }

  /**
   * Paint FEA gradients directly onto the LightSection merged meshes using the
   * per-vertex/per-face FEA attributes baked by the conversor:
   *   - surface mesh: __node_id (per vertex), __elem_id (per face)
   *   - tube mesh:    __node_a_id / __node_b_id / __elem_t (per ring), __elem_id
   * Smoothing is implicit — adjacent faces sharing a FEM node share __node_id
   * and therefore the same color, so gradients are continuous across shared
   * nodes without any node-averaging pass.
   *
   * Paints EVERY mesh unconditionally (see paintFeaColorsOnMesh for why
   * visibility must not be filtered here). Meshes lacking baked attributes
   * (legacy GLBs) are skipped (no-op).
   * Reset is handled by the existing clearVertexColorsFromLightSection().
   */
  public applyFeaColorsToLightSection(
    regions: Array<Region>,
    feaVector: Record<number, number> | Array<number>,
    colorConfig: FeaColorConfiguration,
    options: { byNode: boolean },
    fallbackColor: THREE.Color = new THREE.Color(0x808080)
  ): void {
    const vcMaterial = this.getVertexColorMaterial();
    const colorCache = new Map<number, THREE.Color>(); // shared across meshes

    for (const region of regions) {
      for (const section of region.sections) {
        if (!section.lightSectionGroup) continue;

        section.lightSectionGroup.traverse(child => {
          if (!(child instanceof THREE.Mesh)) return;
          paintFeaColorsOnMesh(child, feaVector as Record<number, number>, colorConfig,
            options.byNode, vcMaterial, fallbackColor, colorCache);
        });
      }
    }
  }

  /** Lazily create a shared MeshStandardMaterial with vertexColors enabled */
  private getVertexColorMaterial(): THREE.MeshStandardMaterial {
    if (!this._vertexColorMaterial) {
      this._vertexColorMaterial = new THREE.MeshStandardMaterial({
        name: 'VertexColorMaterial',
        vertexColors: true,
        polygonOffset: true,
        // Same offset as the structure materials it replaces (BasicMaterial / SECTIONS view modes).
        // With 1/1 the repainted plate sat in front of coplanar inspection areas (1/2) in REGIONS.
        // polygonOffsetFactor: 1,
        // polygonOffsetUnits: 1,
        polygonOffsetFactor: STRUCTURE_VIEW_MODE_Z_OFFSET.factor,
        polygonOffsetUnits: STRUCTURE_VIEW_MODE_Z_OFFSET.units,
        transparent: true,
        opacity: GraphicsMaterials.opacityLevel,
        roughness: 0.85,
        metalness: 0.5,
        side: THREE.DoubleSide,
      });
    }
    return this._vertexColorMaterial;
  }

  public generateRiskLegend(regionsRiskLevels: RegionRisk[], riskLevels: RiskLevelItem[]): void {
    this.legendsService.generateRiskLegend(regionsRiskLevels, riskLevels);
  }

  public degradationViewMode(regions: Array<Region>): void {
    this.legendsService.generateDegradationLegend(regions);
  }

  public remainingLifeViewMode(regions: Array<Region>): void {
    this.legendsService.generateRemainingLifeLegend(regions);
  }

  public corrosionRateViewMode(regions: Array<Region>): void {
    this.legendsService.generateCorrosionRateLegend(regions);
  }

  public corrosionRiskIndexViewMode(regions: Array<Region>): void {
    this.legendsService.generateCorrosionRiskIndexLegend(regions);
  }

  /**
   * Read the already-applied currentMaterial color from each element and
   * apply as vertex colors on merged lightSection meshes.
   * Call after Section-level degradation updates have set element materials.
   */
  public applyCurrentMaterialColorsToLightSection(regions: Array<Region>): void {
    const elementColorMap = this.buildElementColorMap(regions, (element) => {
      const mat = element.currentMaterial as THREE.MeshStandardMaterial;
      return mat?.color ?? null;
    });
    this.applyVertexColorsToLightSection(regions, elementColorMap);
  }

  public structure4DViewMode(regions: Array<Region>): void {
    // Dispose previous 4D structure materials
    Object.values(this.structure4DRenderMaterial).forEach((material) => ThreeJsCleanup.disposeMaterial(material));
    this.structure4DRenderMaterial = {};

    // Collect unique dates from SurfaceElements
    const uniqueDates = new Set<string>();
    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            const dateKey = this.normalizeStructure4DDate(obj.properties?.General?.Collection?.Date);
            if (dateKey) {
              uniqueDates.add(dateKey);
            }
          }
        }
      }
    }

    // Sort chronologically (oldest first)
    const sortedDates = Array.from(uniqueDates).sort(
      (a, b) => new Date(a).getTime()  - new Date(b).getTime()
    );

    // Create materials using HSL darkening from base color 0xB0E0E6
    const baseColor = new THREE.Color(0xB0E0E6);
    const baseHSL = { h: 0, s: 0, l: 0 };
    // baseColor.getHSL(baseHSL);
    baseColor.getHSL(baseHSL, THREE.SRGBColorSpace);

    const n = sortedDates.length;
    const separationBoost = Math.min(Math.max((n - 4) / 12, 0), 1);
    const hueRange = 0.08 + (0.12 * separationBoost);
    const saturationGain = 0.22 + (0.20 * separationBoost);
    const lightnessDrop = 0.38 + (0.18 * separationBoost);
    const alternatingContrast = 0.03 + (0.03 * separationBoost);
    const emissiveStrength = 0.03 + (0.09 * separationBoost);

    for (let i = 0; i < n; i++) {
      const t = n > 1 ? i / (n - 1) : 0;

      const hue = (baseHSL.h + (t * hueRange)) % 1;
      const saturation = Math.min(baseHSL.s + (t * saturationGain), 0.9);
      const contrastLightness = n > 3 ? ((i % 2 === 0 ? 1 : -1) * alternatingContrast * t) : 0;
      const lightness = Math.min(Math.max(baseHSL.l - (t * lightnessDrop) + contrastLightness, 0.17), 0.9);

      // HSL and the emissive scale in sRGB, as three r117 did (see adjustColorVividness)
      // const color = new THREE.Color().setHSL(hue, saturation, lightness);
      const color = new THREE.Color().setHSL(hue, saturation, lightness, THREE.SRGBColorSpace);
      const srgb = color.getRGB({ r: 0, g: 0, b: 0 }, THREE.SRGBColorSpace);
      const emissive = new THREE.Color().setRGB(srgb.r * emissiveStrength, srgb.g * emissiveStrength, srgb.b * emissiveStrength, THREE.SRGBColorSpace);

      const material = GraphicsMaterials.withOffset(THREE.MeshStandardMaterial, {
        name: 'Structure4DMaterial',
        color: color,
        // emissive: color.clone().multiplyScalar(emissiveStrength),
        emissive: emissive,
        emissiveIntensity: 0.6,
        transparent: true,
        opacity: GraphicsMaterials.opacityLevel,
        roughness: 0.85,
        metalness: 0.5,
        side: THREE.DoubleSide,
      });
      // Each newer date (higher i) gets a lower units offset so it renders on top of older dates
      material.polygonOffsetUnits = material.polygonOffsetUnits - (i * 2);
      // Rename after creation so each date has a unique name for debug GUI
      material.name = `Structure4DMaterial_${i}`;
      this.structure4DRenderMaterial[sortedDates[i]] = material;

      // Register with debug GUI if available
      if (this.debugGui) {
        this.debugGui.addCustomMaterial(`structure4D_${sortedDates[i]}`, material);
      }
    }

    // Assign materials to SurfaceElements only
    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            const dateKey = this.normalizeStructure4DDate(obj.properties?.General?.Collection?.Date);
            if (dateKey && this.structure4DRenderMaterial[dateKey]) {
              obj.SurfaceDesign.material = this.structure4DRenderMaterial[dateKey];
              obj.currentMaterial = this.structure4DRenderMaterial[dateKey];
            }
          }
        }
      }
    }

    // Color merged meshes in lightSectionGroup for MODEL/REGIONS modes
    const elementColorMap = this.buildElementColorMap(regions, (element) => {
      if (!(element instanceof SurfaceElement)) return null;
      const dateKey = this.normalizeStructure4DDate(element.properties?.General?.Collection?.Date);
      const mat = this.structure4DRenderMaterial[dateKey] as THREE.MeshStandardMaterial;
      return mat?.color ?? null;
    });
    this.applyVertexColorsToLightSection(regions, elementColorMap);

    this.legendsService.generateStructure4DLegend(regions, this.structure4DRenderMaterial);
  }

  public regenerateStructure4DLegend(regions: Array<Region>): void {
    this.legendsService.generateStructure4DLegend(regions, this.structure4DRenderMaterial);
  }

  private normalizeStructure4DDate(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const text = String(value).trim();
    const isoPrefixMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoPrefixMatch) {
      return isoPrefixMatch[1];
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public regenerateThicknessLegend(regions: Array<Region>): void {
    this.legendsService.generateThicknessLegend(regions, this.thicknessRenderMaterial);
  }

  public regenerateMaterialsLegend(regions: Array<Region>): void {
    this.legendsService.generateMaterialsLegend(regions, this.materialsRenderMaterial);
  }

  public regenerateRegionsLegend(regions: Array<Region>): void {
    this.legendsService.generateRegionsLegend(regions, this.regionsRenderMaterial);
  }

  public static riskViewMode(regions: Region[], regionRisks: RegionRisk[], riskLevels: RiskLevelItem[]): void {
    // Guard clauses
    if (!Array.isArray(regions) || regions.length === 0) {
      return;
    }

    if (!Array.isArray(regionRisks) || regionRisks.length === 0) {
      return;
    }

    if (!Array.isArray(riskLevels) || riskLevels.length === 0) {
      return;
    }

    const highestRiskLevelId = GraphicsMaterials.getHighestRiskLevelId(riskLevels);

    regions.forEach(region => {
      const regionRisk = regionRisks.find(info => info.regionId == region.Guid)
      if (regionRisk) {
        if(regions.length > 1){
          for (const e of region.Elements) {
            var riskMaterial = GraphicsMaterials.getRiskMaterial(regionRisk.riskLevelId, riskLevels);
            if (regionRisk.riskLevelId === highestRiskLevelId) {
              const risk = GraphicsMaterials.getRegionRisk(regionRisk, riskLevels);
              riskMaterial = GraphicsMaterials.getRiskMaterial(risk, riskLevels);
            }
            if (riskMaterial) {
              e.SurfaceDesign.material = riskMaterial
              e.currentMaterial = riskMaterial
            }
          }
          return

        }
        region.sections.forEach(section => {
          const sectionInfo = regionRisk.sections.find(sInfo => sInfo.sectionId == section.Guid)
          const sectionRisk = GraphicsMaterials.getSectionRisk(sectionInfo, riskLevels)
          if (sectionRisk) {
            var riskMaterial = GraphicsMaterials.getRiskMaterial(sectionRisk, riskLevels);
            if (riskMaterial) {
              for (const e of section.surfaceElements) {
                e.SurfaceDesign.material = riskMaterial
                e.currentMaterial = riskMaterial
              }
              for (const e of section.linearElements) {
                e.SurfaceDesign.material = riskMaterial
                e.currentMaterial = riskMaterial
              }
            }
          }
        })
      }
    })
  }

  private static getHighestRiskLevelId(riskLevels: RiskLevelItem[]): string {
    return riskLevels.reduce((highest, current) =>
      current.order > highest.order ? current : highest
    ).id;
  }

  private static getRegionRisk(regionRisk: RegionRisk, riskLevels: RiskLevelItem[]): string {
    const highestRiskLevelId = GraphicsMaterials.getHighestRiskLevelId(riskLevels);
    if (regionRisk.riskLevelId === highestRiskLevelId) {
      var allCriticalAreas = regionRisk.sections.flatMap(s => s.criticalAreas);
      const risk = GraphicsMaterials.getElementRisk(allCriticalAreas, riskLevels)
      return risk
    }
    return regionRisk.riskLevelId
  }

  private static getSectionRisk(sectionRisk: SectionRisk, riskLevels: RiskLevelItem[]): string {
    const highestRiskLevelId = GraphicsMaterials.getHighestRiskLevelId(riskLevels);
    if (sectionRisk.riskLevelId === highestRiskLevelId) {
      var risk = GraphicsMaterials.getElementRisk(sectionRisk.criticalAreas, riskLevels)
      return risk
    }

    return sectionRisk.riskLevelId
  }

  private static getElementRisk(criticalAreas: ICriticalAreaDTO[], riskLevels: RiskLevelItem[]) {
    var riskElements: { [key: string]: ICriticalAreaDTO[] } = {};

    riskLevels.forEach(rl => {
      riskElements[rl.id] = [];
    });

    criticalAreas.forEach(ca => {
      if (riskElements[ca.riskLevelId]) {
        riskElements[ca.riskLevelId].push(ca);
      }
    });

    const sortedRiskLevels = [...riskLevels].sort((a, b) => b.order - a.order);

    for (const riskLevel of sortedRiskLevels) {
      if (riskElements[riskLevel.id].length > 0) {
        return riskLevel.id;
      }
    }

    return sortedRiskLevels[sortedRiskLevels.length - 1].id; // Return the lowest risk level if no critical areas found
  }

  private static calculateRiskPolygonOffset(riskLevel: RiskLevelItem, riskLevels: RiskLevelItem[]): { factor: number, units: number } {
    const baseRiskOffset = -6;

    const sortedRiskLevels = [...riskLevels].sort((a, b) => a.order - b.order);

    const riskLevelIndex = sortedRiskLevels.findIndex(rl => rl.id === riskLevel.id);

    const offsetIncrement = 2;

    const orderOffset = (riskLevelIndex  + 1) * offsetIncrement;

    return {
      factor: baseRiskOffset - orderOffset,
      units: baseRiskOffset - orderOffset
    };
  }

  public static getRiskMaterial(riskLevelId: string, riskLevels: RiskLevelItem[]): THREE.MeshBasicMaterial | null {
    const riskLevel = riskLevels.find(rl => rl.id === riskLevelId);
    if (riskLevel) {
      const baseColor = new THREE.Color(riskLevel.color);
      const adjustedColor = GraphicsMaterials.adjustColorVividness(baseColor);

      const polygonOffset = GraphicsMaterials.calculateRiskPolygonOffset(riskLevel, riskLevels);

      const materialName = `RiskMaterial_${riskLevel.name}`;
      if (!GraphicsMaterials.criticalAreasOffset.has(materialName)) {
        GraphicsMaterials.criticalAreasOffset.set(materialName, polygonOffset);
      }

      return new THREE.MeshBasicMaterial({
        name: materialName,
        color: adjustedColor,
        ...GraphicsMaterials.commonRiskMatProp,
        polygonOffset: true,
        polygonOffsetFactor: polygonOffset.factor,
        polygonOffsetUnits: polygonOffset.units
      });
    }
    return null;
  }

  private static adjustColorVividness(color: THREE.Color): THREE.Color {
    // Convert the color to HSL
    // HSL in sRGB, as three r117 did (no colour management). The 0.182 default is the
    // linear working space, which washed the risk colours out (#ff0000 -> #ed6c6c instead of #d92626).
    const hsl = { h: 0, s: 0, l: 0 };
    // color.getHSL(hsl);
    color.getHSL(hsl, THREE.SRGBColorSpace);

    // Adjust saturation and lightness
    hsl.s = Math.min(hsl.s, 0.7);  // Cap saturation at 70%
    hsl.l = Math.max(Math.min(hsl.l, 0.7), 0.3);  // Keep lightness between 30% and 70%

    // Create a new color from the adjusted HSL values
    // return new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l);
    return new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l, THREE.SRGBColorSpace);
  }

  public static updateCriticalAreaRiskMaterial(criticalAreas: CriticalAreaElement[], regionRisks: RegionRisk[], riskLevels: RiskLevelItem[]) {
    criticalAreas.forEach(ca => {
      let regionId = ca.regionId || (ca.regionIds.length > 0 ? ca.regionIds[0] : null);
      if (regionId) {
        const sectionId = ca.SectionId;
        const regionRisk = regionRisks.find(r => r.regionId === regionId);
        if (regionRisk) {
          const sectionRisk = regionRisk.sections.find(s => s.sectionId === sectionId);
          if (sectionRisk && ca?.CriticalAreaProperties?.CriticalAreaId) {
            const caRisk = sectionRisk.criticalAreas.find(a => a.id === ca.CriticalAreaProperties.CriticalAreaId);
            const riskLevelId = caRisk?.riskLevelId;
            if (riskLevelId) {
              const newMat = GraphicsMaterials.getRiskMaterial(riskLevelId, riskLevels);
              if (newMat) {
                ca.SurfaceDesign.material = newMat;
                ca.currentMaterial = newMat;
              }
            }
          }
        }
      }
    });
  }

  public transparenceViewMode(regions: Array<Region>): void {
    GraphicsMaterials.basicMaterial.depthWrite = false;
    this.applyLightSectionTransparence(regions, true);
  }

  /**
   * Transparence is plates without depth writes, so whatever is drawn after them paints over
   * them. On prod (one mesh per element, scene order) each section's plates are drawn before
   * its stiffeners/brackets/face plates, which is what makes the internal structure show
   * through. The merged lightsection meshes (REGIONS / MODEL on new-format assets) render
   * through mask clones that did not see depthWrite, and draw the plates last
   * (SurfaceTypesMesh renderOrder 1 vs 0, Section.applyTypeMaterial) — so the mode did nothing.
   */
  private applyLightSectionTransparence(regions: Array<Region>, enabled: boolean): void {
    syncMaskedMaterialsFromBase(); // mask clones follow basicMaterial.depthWrite
    for (const region of regions ?? []) {
      for (const section of region.sections) {
        section.lightSectionGroup?.traverse(child => {
          if (!(child instanceof THREE.Mesh) || child.name.split('_')[0] !== 'SurfaceTypesMesh') return;
          child.renderOrder = enabled ? -1 : 1; // 1 = Section.applyTypeMaterial default
        });
      }
    }
  }

  public setRegionAsSelected(region: Region) {
    region.selected = true;
    for (const section of region.sections) {
      for (const obj of section.surfaceElements) {
        if (obj instanceof SurfaceElement) {
          obj.SurfaceDesign.material = GraphicsMaterials.selectedBasicMaterial;
        }
      }
    }
  }

  public clearRegionAsSelected(region: Region) {
    region.selected = false;
    for (const section of region.sections) {
      section.selected = false;
      for (const obj of section.surfaceElements) {
        if (obj instanceof SurfaceElement) {
          if (obj.isHighlightedByFilterStructure) {
            obj.SurfaceDesign.material = GraphicsMaterials.structureMaterial
          } else {
            obj.SurfaceDesign.material = obj.currentMaterial
          }
        };

      }
    }
  }

  public setSectionAsSelected(region: Region, sectionId: string) {
    const section = region.sections.find(x => x.Guid === sectionId);
    section.selected = true;
    for (const obj of section?.surfaceElements) {
      if (obj instanceof SurfaceElement) obj.SurfaceDesign.material = GraphicsMaterials.selectedBasicMaterial;

    }
  }

  public clearSectionAsSelected(region: Region, sectionId: string) {
    const section = region.sections.find(x => x.Guid === sectionId);
    if (section) {
      section.selected = false;
      for (const obj of section?.surfaceElements) {
        if (obj instanceof SurfaceElement) {
          if (obj.isHighlightedByFilterStructure) {
            obj.SurfaceDesign.material = GraphicsMaterials.structureMaterial
          } else {
            obj.SurfaceDesign.material = obj.currentMaterial
          }
        }
      }
    }
  }

  public setElementAsSelected(region: Region, sectionId: string, elementId: string) {
    const section = region.sections.find(x => x.Guid === sectionId);
    if (section) {
      const element = section?.surfaceElements?.find(x => (x as SurfaceElement).Guid === elementId);
      if (element) (element as SurfaceElement).SurfaceDesign.material = GraphicsMaterials.selectedBasicMaterial;
      else {
        const lineElement = section?.linearElements?.find(x => (x as SurfaceElement).Guid === elementId);
        if (lineElement) (lineElement as LinearElement).SurfaceDesign.material = GraphicsMaterials.selectedBasicMaterial;
      }
    }
  }

  public completeIds(regions: Array<Region>): void {
    for (const region of regions) {
      for (const section of region.sections) {
        var idsLooked = [];
        var stiffeners = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.STIFFENER);
        var brackets = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.BRACKET);
        for (const obj of stiffeners) {
          if (obj instanceof LinearElement) {
            var line = obj as LinearElement;
            if(!line.properties.General.CompleteStiffenerId)
              continue;
            if (idsLooked.includes(line.properties.General.CompleteStiffenerId))
              continue;
            idsLooked.push(line.properties.General.CompleteStiffenerId);
            var newMat = GraphicsMaterials.randomMaterial.clone();
            for (const e of stiffeners) {
              if(e.properties.General.CompleteStiffenerId === line.properties.General.CompleteStiffenerId){
                e.currentRenderMaterial = newMat;
              }
            }
          }
        }
        for (const obj of brackets) {
          if (obj instanceof LinearElement) {
            var line = obj as LinearElement;
            if(!line.properties.General.CompleteBracketId)
              continue;
            if (idsLooked.includes(line.properties.General.CompleteBracketId))
              continue;
            idsLooked.push(line.properties.General.CompleteBracketId);
            var newMat = GraphicsMaterials.randomMaterial.clone();
            for (const e of brackets) {
              if(e.properties.General.CompleteBracketId === line.properties.General.CompleteBracketId){
                e.currentRenderMaterial = newMat;
              }
            }
          }
        }
      }
    }
  }
  public fullIds(regions: Array<Region>): void {
    for (const region of regions) {
      for (const section of region.sections) {
        var idsLooked = [];
        var stiffeners = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.STIFFENER);
        var brackets = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.BRACKET);
        for (const obj of stiffeners) {
          if (obj instanceof LinearElement) {
            var line = obj as LinearElement;
            if(!line.properties?.General?.FullStiffenerId)
              continue;
            if (idsLooked.includes(line.properties?.General?.FullStiffenerId))
              continue;
            idsLooked.push(line.properties.General.FullStiffenerId);
            var newMat = GraphicsMaterials.randomMaterial.clone();
            for (const e of stiffeners) {
              if(e.properties.General.FullStiffenerId === line.properties.General.FullStiffenerId){
                e.currentRenderMaterial = newMat;
              }
            }
          }
        }
        for (const obj of brackets) {
          if (obj instanceof LinearElement) {
            var line = obj as LinearElement;
            if(!line.properties?.General?.FullBracketId)
              continue;
            if (idsLooked.includes(line.properties?.General?.FullBracketId))
              continue;
            idsLooked.push(line.properties?.General?.FullBracketId);
            var newMat = GraphicsMaterials.randomMaterial.clone();
            for (const e of brackets) {
              if(e.properties.General.FullBracketId === line.properties.General.FullBracketId){
                e.currentRenderMaterial = newMat;
              }
            }
          }
        }
      }
    }
  }

  public flangeElementsView(regions: Array<Region>): void {
    for (const region of regions) {
      for (const section of region.sections) {
        var stiffeners = section.linearElements.filter(x => (x.TypeId === ELEMENTSTYPESIDS.STIFFENER ||  x.TypeId === ELEMENTSTYPESIDS.BRACKET) &&
          x.properties && x.properties.Geometry.FlangeThickness > 0);
          const groupedElements = stiffeners.reduce((groups, element) => {
            const guid = element.Guid;
            if (!groups[guid]) {
              groups[guid] = [];
            }
            groups[guid].push(element);
            return groups;
          }, {} as { [key: string]: WorkableElement[] });

          for (const guid in groupedElements) {
            var newMat = GraphicsMaterials.randomMaterial.clone();

            for (const line of groupedElements[guid]) {
              line.currentRenderMaterial = newMat;
            }
          }
      }
    }
  }

  public stiffenersBracketsView(regions: Array<Region>): void {
    for (const region of regions) {
      for (const section of region.sections) {
        var stiffeners = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.STIFFENER);
        var brackets = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.BRACKET);


        for (const obj of stiffeners) {
          if (obj instanceof LinearElement) {
            var line = obj as LinearElement;
            line.currentRenderMaterial = GraphicsMaterials.greenMaterial.clone();
          }
        }
        for (const obj of brackets) {
          if (obj instanceof LinearElement) {
            var line = obj as LinearElement;
            if (obj instanceof LinearElement) {
              var line = obj as LinearElement;
              line.currentRenderMaterial = GraphicsMaterials.redMaterial.clone();
            }
          }
        }
      }
    }
  }

  private typicalDetailsFamilyRenderMaterial: Record<string, THREE.Material> = {};

  public typicalDetailsFamilyView(regions: Array<Region>): void {
    const familyRenderMaterial: Record<string, THREE.Material> = {};

    for (const region of regions) {
      for (const section of region.sections) {
        const familyGuidsLookedUp: string[] = [];
        const brackets = section.linearElements.filter(x => x.TypeId === ELEMENTSTYPESIDS.BRACKET);

        for (const obj of brackets) {
          if (obj instanceof LinearElement) {
            const line = obj as LinearElement;

            // Check if the bracket has TypicalDetail property
            const typicalDetail = line.properties?.TypicalDetail;
            if (!typicalDetail?.FamilyGuid) {
              continue;
            }

            // Skip if we've already processed this family
            if (familyGuidsLookedUp.includes(typicalDetail.FamilyGuid)) {
              continue;
            }

            familyGuidsLookedUp.push(typicalDetail.FamilyGuid);

            // Use FamilyColor if available, otherwise generate random color
            let newMat: THREE.MeshBasicMaterial;
            if (typicalDetail.FamilyColor) {
              const colorHex = typicalDetail.FamilyColor.replace('#', '0x');
              newMat = new THREE.MeshBasicMaterial({
                name: 'TypicalDetailsFamilyMaterial',
                color: Number(colorHex),
                side: THREE.DoubleSide
              });
            } else {
              newMat = GraphicsMaterials.randomMaterial.clone();
            }

            familyRenderMaterial[typicalDetail.FamilyGuid] = newMat;

            // Apply the material to all brackets in this family
            for (const bracket of brackets) {
              const bracketTypicalDetail = bracket.properties?.TypicalDetail;
              if (bracketTypicalDetail?.FamilyGuid === typicalDetail.FamilyGuid) {
                bracket.currentRenderMaterial = newMat;
              }
            }
          }
        }
      }
    }

    this.typicalDetailsFamilyRenderMaterial = familyRenderMaterial;
    this.legendsService.generateTypicalDetailsFamilyLegend(regions, familyRenderMaterial);
  }

  public regenerateTypicalDetailsFamilyLegend(regions: Array<Region>): void {
    this.legendsService.generateTypicalDetailsFamilyLegend(regions, this.typicalDetailsFamilyRenderMaterial);
  }

  public clearElementAsSelected(region: Region, sectionId: string, elementId: string) {
    const section = region.sections.find(x => x.Guid === sectionId);
    if (section) {
      const element = section?.surfaceElements?.find(x => (x as SurfaceElement).Guid === elementId);
      const gaugingElement = element as GaugingElement
      if (element) {
        if (gaugingElement.isHighlightedByFilter == false) {
          (element as SurfaceElement).SurfaceDesign.material = (element as SurfaceElement).currentMaterial;
          return;
        }
        if (gaugingElement.isHighlightedByFilter == true) {
          return;
        }
        if (element.isHighlightedByFilterStructure == false) {
          (element as LinearElement).SurfaceDesign.material = (element as LinearElement).currentMaterial
          return;
        }
        if (element.isHighlightedByFilterStructure == true) {
          (element as LinearElement).SurfaceDesign.material = GraphicsMaterials.structureMaterial
          return;
        }
      }
      else {
        const lineElement = section?.linearElements?.find(x => (x as SurfaceElement).Guid === elementId);
        if (lineElement) {
          if (lineElement.isHighlightedByFilterStructure) {
            (lineElement as LinearElement).SurfaceDesign.material = GraphicsMaterials.structureMaterial
          } else {
            (lineElement as LinearElement).SurfaceDesign.material = (lineElement as LinearElement).currentMaterial
          }
        };
      }
    }
  }

  public setSurfaceElementAsSelected(surfaceElement: WorkableElement) {
    if (surfaceElement) surfaceElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicMaterial;
  }

  public setGaugingElementAsSelected(surfaceElement: WorkableElement) {
    if (surfaceElement) surfaceElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicElementMaterial;
  }

  public setLinearElementAsSelected(lineElement: LinearElement) {
    if (lineElement) lineElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicMaterial;
  }

  public getSpriteMaterial(iconType: string): THREE.SpriteMaterial {
    switch (iconType) {
      case "red fas fa-file-pdf":
        return this.pdfSpriteMaterial;
      case "fas fa-file-image":
        return this.imgSpriteMaterial;
      case "green fas fa-file-excel":
        return this.exlSpriteMaterial;
      case "blue fas fa-file-word":
        return this.wrdSpriteMaterial;
      case "red fab fa-youtube":
        return this.ytbSpriteMaterial;
      default:
        return this.altSpriteMaterial;
    }
  }

  // public get spriteWidth(): number {
  //   return this.pdfSpriteMaterial.map.image.width
  // }

  // public get spriteHeight(): number {
  //   return this.pdfSpriteMaterial.map.image.width
  // }

  /**
   * The merged lightsection meshes (new-format assets) render through per-mesh
   * patched clones of the static materials (see lightsection-mask.ts). Any code
   * that mutates the statics' visible/opacity must call this afterwards or the
   * merged plates ignore the change (X-ray modes, box-measurement tool).
   */
  public static syncLightSectionMaskMaterials(): void {
    syncMaskedMaterialsFromBase();
  }

  public setStructureMaterialsVisibility(v: boolean): void {
    GraphicsMaterials.basicMaterial.visible = v;
    GraphicsMaterials.stiffenerMaterial3d.visible = v;
    GraphicsMaterials.flangeMaterial3d.visible = v;
    GraphicsMaterials.bracketMaterial3d.visible = v;
    GraphicsMaterials.hatchingMaterial.visible = v;
    GraphicsMaterials.repairBasicMaterial.visible = v;
    GraphicsMaterials.doublerBasicMaterial.visible = v;
    GraphicsMaterials.doublerStiffenerMaterial3d.visible = v;
    GraphicsMaterials.doublerFlangeMaterial3d.visible = v;
    GraphicsMaterials.doublerBracketMaterial3d.visible = v;
    GraphicsMaterials.doublerBeamMaterial3d.visible = v;
    GraphicsMaterials.doublerExecutedRepairMaterial.visible = v;
    GraphicsMaterials.syncLightSectionMaskMaterials();
  }

  /**
   * Toggle visibility of type meshes in lightSectionGroup for elements view filtering.
   * All types — including the plate (SurfaceTypesMesh) — use mesh.visible directly.
   * Type meshes are siblings of SurfaceTypesMesh, so hiding the plate does not hide them.
   */
  public filterLightSectionElementTypes(regions: Array<Region>, elementTypes: string[]): void {
    const plateVisible = elementTypes.includes(ELEMENTSTYPESIDS.PLATE);

    // Disable occlusion culling when plates are hidden (culler targets SurfaceTypesMesh)
    this.cullingService.enabled = plateVisible;

    for (const region of regions) {
      for (const section of region.sections) {
        if (!section.lightSectionGroup) continue;

        section.lightSectionGroup.traverse(child => {
          if (!(child instanceof THREE.Mesh)) return;
          const meshName = child.name?.split('_')[0];

          // Plate (SurfaceTypesMesh): toggle .visible like any other type. It is
          // now a sibling of the other type meshes, so hiding it does not hide them.
          if (meshName === 'SurfaceTypesMesh') {
            child.visible = plateVisible;
            return;
          }

          // Child type meshes: toggle .visible by matching type GUID in name
          child.visible = elementTypes.includes(meshName);
        });
      }
    }
  }

  public setStructureMaterialsVisibilityCriticalArea(v: boolean): void {
    GraphicsMaterials.basicMaterial.visible = v;
    GraphicsMaterials.stiffenerMaterial3d.visible = v;
    GraphicsMaterials.flangeMaterial3d.visible = v;
    GraphicsMaterials.bracketMaterial3d.visible = v;
    GraphicsMaterials.hatchingMaterial.visible = v;
    GraphicsMaterials.repairBasicMaterial.visible = v;
    GraphicsMaterials.inspectionMaterial.visible = v;
    GraphicsMaterials.doublerBasicMaterial.visible = v;
    GraphicsMaterials.doublerStiffenerMaterial3d.visible = v;
    GraphicsMaterials.doublerFlangeMaterial3d.visible = v;
    GraphicsMaterials.doublerBracketMaterial3d.visible = v;
    GraphicsMaterials.doublerBeamMaterial3d.visible = v;
    GraphicsMaterials.doublerExecutedRepairMaterial.visible = v;
    GraphicsMaterials.syncLightSectionMaskMaterials();
  }

  public setStructureMaterialsVisibilityRepair(v: boolean): void {
    GraphicsMaterials.basicMaterial.visible = v;
    GraphicsMaterials.stiffenerMaterial3d.visible = v;
    GraphicsMaterials.flangeMaterial3d.visible = v;
    GraphicsMaterials.bracketMaterial3d.visible = v;
    GraphicsMaterials.doublerBasicMaterial.visible = v;
    GraphicsMaterials.doublerStiffenerMaterial3d.visible = v;
    GraphicsMaterials.doublerFlangeMaterial3d.visible = v;
    GraphicsMaterials.doublerBracketMaterial3d.visible = v;
    GraphicsMaterials.doublerBeamMaterial3d.visible = v;
    GraphicsMaterials.doublerExecutedRepairMaterial.visible = v;
    GraphicsMaterials.syncLightSectionMaskMaterials();
  }

  public setStructureMaterialsOpacity(opacity: number): void {
    GraphicsMaterials.basicMaterial.opacity = opacity;
    GraphicsMaterials.basicPhysicalMaterial.opacity = opacity;
    GraphicsMaterials.stiffenerMaterial3d.opacity = opacity;
    GraphicsMaterials.flangeMaterial3d.opacity = opacity;
    GraphicsMaterials.bracketMaterial3d.opacity = opacity;
    GraphicsMaterials.hatchingMaterial.opacity = opacity;
    GraphicsMaterials.doublerBasicMaterial.opacity = opacity;
    GraphicsMaterials.doublerExecutedRepairMaterial.opacity = opacity;
    GraphicsMaterials.doublerStiffenerMaterial3d.opacity = opacity;
    GraphicsMaterials.doublerFlangeMaterial3d.opacity = opacity;
    GraphicsMaterials.doublerBracketMaterial3d.opacity = opacity;
    GraphicsMaterials.doublerBeamMaterial3d.opacity = opacity;
    GraphicsMaterials.syncLightSectionMaskMaterials();
  }

  public static colorToBasicMaterial(color: string | number) {
    return new THREE.MeshBasicMaterial({
      name: "ColorMaterial " + color,
      color: color,
      side: THREE.DoubleSide
    })
  }

  public static getSensorResultBarMaterial(color?: string | number) {
    if (color == null) {
      return new THREE.MeshStandardMaterial({
        name: "SensorResultBarMaterial",
        color: 0x555555,
        transparent: true,
        opacity: 0.75,
        roughness: 0.9,
        metalness: 0.4,
        side: THREE.DoubleSide,
      });
    }
    else {
      return new THREE.MeshStandardMaterial({
        name: "SensorResultBarMaterial " + color,
        color: color,
        transparent: true,
        opacity: 0.75,
        roughness: 0.9,
        metalness: 0.4,
        side: THREE.DoubleSide,
      });
    }
  }

  private static createLightModelEdgeMaterial(): THREE.LineBasicMaterial {
    const material = new THREE.LineBasicMaterial({
      name: "LightModelEdgeMaterial",
      color: 0x333333,
      transparent: false,
      opacity: 1,
      linewidth: 1,
      depthFunc: THREE.LessEqualDepth,
    });
    const bias = LIGHT_MODEL_EDGE_DEPTH_BIAS.toFixed(9);
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader.replace(
        '#include <project_vertex>',
        `#include <project_vertex>\n\tgl_Position.z -= ${bias} * gl_Position.w;`,
      );
    };
    material.customProgramCacheKey = () => 'LightModelEdgeMaterial:' + bias;
    return material;
  }

  private static withOffset<T extends THREE.Material>(
    MaterialClass: new (params: any) => T,
    params: any
  ): T {
    const offset = MATERIAL_Z_OFFSETS[params.name] || MATERIAL_Z_OFFSETS['default'];

    return new MaterialClass({
      ...params,
      polygonOffset: true,
      polygonOffsetFactor: offset.factor,
      polygonOffsetUnits: offset.units,
    });
  }



  public static isSupportedBringToFrontMaterial(materialName: string | null | undefined): materialName is SupportedBringToFrontMaterialName {
    if (!materialName) return false;

    if (SUPPORTED_BRING_TO_FRONT_MATERIALS.includes(materialName as any)) {
      return true;
    }

    if (this.isRiskMaterial(materialName)) {
      return true;
    }

    return false;
  }

  public static isRiskMaterial(materialName: string): materialName is RiskMaterialNamePattern {
    return materialName.startsWith('RiskMaterial_');
  }

  public static bringElementToFront(workableElement: WorkableElement): void {
    if (!workableElement?.SurfaceDesign?.material) {
      console.warn('WorkableElement does not have a valid material to modify');
      return;
    }

    const material = workableElement.SurfaceDesign.material as THREE.Material;

    if (!this.isSupportedBringToFrontMaterial(material.name)) {
      console.log(`Material '${material.name}' does not support bring-to-front functionality`);
      return;
    }

    // Store the current material before overriding for restoration
    workableElement.materialBeforeFront = workableElement.currentMaterial;

    // Clone the material to avoid affecting other elements using the same material
    const frontMaterial = material.clone();

    const highestPriorityOffset = { factor: -100, units: -100 };

    frontMaterial.polygonOffset = true;
    frontMaterial.polygonOffsetFactor = highestPriorityOffset.factor;
    frontMaterial.polygonOffsetUnits = highestPriorityOffset.units;
    frontMaterial.needsUpdate = true;

    // Assign the cloned material to this specific element
    workableElement.currentRenderMaterial = frontMaterial;

    console.log(`Element brought to front with cloned material ${material.name} and offset: ${highestPriorityOffset.factor}, ${highestPriorityOffset.units}`);
  }

  public static restoreElementToNormal(workableElement: WorkableElement): void {
    if (!workableElement?.SurfaceDesign?.material) {
      console.warn('WorkableElement does not have a valid material to restore');
      return;
    }

    if (workableElement.materialBeforeFront) {
      const beforeFrontMaterial = workableElement.materialBeforeFront as THREE.Material;
      const currentMat = workableElement.currentMaterial as THREE.Material;

      const restoredMaterial = currentMat.clone();
      restoredMaterial.polygonOffset = beforeFrontMaterial.polygonOffset;
      restoredMaterial.polygonOffsetFactor = beforeFrontMaterial.polygonOffsetFactor;
      restoredMaterial.polygonOffsetUnits = beforeFrontMaterial.polygonOffsetUnits;
      restoredMaterial.needsUpdate = true;

      workableElement.currentRenderMaterial = restoredMaterial;

      workableElement.materialBeforeFront = null;

      console.log(`Element restored to normal with merged offset: ${restoredMaterial.polygonOffsetFactor}, ${restoredMaterial.polygonOffsetUnits}`);
    } else {
      workableElement.currentRenderMaterial = workableElement.currentMaterial;
      console.log(`Element restored to normal using current material`);
    }
  }

  // Debug GUI Methods
  public initializeDebugGui(scene?: THREE.Scene): void {
    if (!environment.debugEnabled) {
      console.log('🚫 Debug GUI disabled for this environment');
      return;
    }

    if (!this.debugGui) {
      this.debugGui = new GraphicsDebug(scene);
    } else if (scene) {
      this.debugGui.setScene(scene);
    }
  }

  public showDebugGui(): void {
    if (!environment.debugEnabled) {
      console.log('🚫 Debug GUI disabled for this environment');
      return;
    }

    if (!this.debugGui) {
      this.initializeDebugGui();
    }
    this.debugGui!.show();
  }

  public hideDebugGui(): void {
    if (this.debugGui) {
      this.debugGui.hide();
    }
  }

  public toggleDebugGui(): void {
    if (!environment.debugEnabled) {
      console.log('🚫 Debug GUI disabled for this environment');
      return;
    }

    if (!this.debugGui) {
      this.initializeDebugGui();
    }
    this.debugGui!.toggle();
  }

  public addCustomMaterialToDebug(name: string, material: THREE.Material): void {
    if (!this.debugGui) {
      this.initializeDebugGui();
    }
    this.debugGui!.addCustomMaterial(name, material);
  }

  public exportDebugSettings(): string {
    if (!this.debugGui) {
      return '{}';
    }
    return this.debugGui.exportSettings();
  }

  public importDebugSettings(settingsJson: string): void {
    if (!this.debugGui) {
      this.initializeDebugGui();
    }
    this.debugGui!.importSettings(settingsJson);
  }

  public refreshDebugMaterials(): void {
    if (this.debugGui) {
      this.debugGui.refreshMaterials();
    }
  }

  public setDebugScene(scene: THREE.Scene): void {
    if (this.debugGui) {
      this.debugGui.setScene(scene);
    }
  }
}
